// src/modals/UtilizationDetailsModal.jsx
import { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import BaseExpandedCard from '../components/cards/BaseExpandedCard';
import ProjectNameLink from '../components/common/ProjectNameLink';
import ClientNameLink from '../components/common/ClientNameLink';
import { TrendIndicator, LoadingState, ErrorState, EmptyState, StatCard } from '../components/common';
import { useUtilizationPeriods, PERIOD_OPTIONS } from '../hooks/useUtilizationPeriods';
import { getUtilizationColor } from '../utils/colorHelpers';
import { formatDate, formatPercentage, formatHours } from '../utils/formatters';
import { API_BASE_URL } from '../config';
import { COLOR_WARN, TEXT_SECONDARY } from '../utils/colors';

// ── main component ───────────────────────────────────────────────────────────

const UtilizationDetailsModal = ({ open, onClose, employeeName, employeeId, tabMode = false }) => {
  const {
    selectedPeriod,
    setSelectedPeriod,
    currentPeriodData,
    loading,
    error
  } = useUtilizationPeriods(API_BASE_URL, employeeId, open);

  const handlePeriodChange = useCallback((newPeriod) => {
    if (newPeriod !== null) setSelectedPeriod(newPeriod);
  }, [setSelectedPeriod]);

  const utilizationColor = useMemo(() => {
    return getUtilizationColor(currentPeriodData.average || 0);
  }, [currentPeriodData.average]);

  // Compute period totals from weekly data
  const totals = useMemo(() => {
    const weeks = currentPeriodData.weekly_data || [];
    return {
      clientHours:   weeks.reduce((s, w) => s + (w.billable_hours     || 0), 0),
      internalHours: weeks.reduce((s, w) => s + (w.non_billable_hours || 0), 0),
    };
  }, [currentPeriodData.weekly_data]);

  const Wrap = ({ children }) => tabMode
    ? <>{children}</>
    : (
      <BaseExpandedCard open={open} onClose={onClose} title={`Utilization Details: ${employeeName}`} subtitle={`Billable Hours Utilization (${currentPeriodData.unique_weeks || 0} weeks)`}>
        {children}
      </BaseExpandedCard>
    );

  if (loading) return <Wrap><LoadingState message="Fetching utilization data for all periods..." /></Wrap>;
  if (error)   return <Wrap><ErrorState message={error} /></Wrap>;

  return (
    <Wrap>
      {/* ── Period selector ──────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            value={selectedPeriod}
            onChange={(e) => handlePeriodChange(e.target.value)}
            sx={{ fontSize: '0.8rem' }}
          >
            {PERIOD_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* ── Summary stat cards ───────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <StatCard label="Utilization">
          <Typography variant="h5" sx={{ fontWeight: 700, color: utilizationColor, lineHeight: 1 }}>
            {currentPeriodData.average != null ? formatPercentage(currentPeriodData.average, 2) : 'N/A'}
          </Typography>
        </StatCard>

        <StatCard label="Client Hours">
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
            {Math.round(totals.clientHours)}
          </Typography>
        </StatCard>

        <StatCard label="Internal Hours">
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
            {Math.round(totals.internalHours)}
          </Typography>
        </StatCard>

        <StatCard label="Weeks in Period">
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
            {currentPeriodData.unique_weeks || 0}
          </Typography>
        </StatCard>

        <StatCard label="Trend">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, textTransform: 'capitalize', lineHeight: 1.2 }}>
              {currentPeriodData.trend === 'neutral' ? 'Stable'
                : currentPeriodData.trend
                  ? currentPeriodData.trend.charAt(0).toUpperCase() + currentPeriodData.trend.slice(1)
                  : '—'}
            </Typography>
            {currentPeriodData.trend && <TrendIndicator trend={currentPeriodData.trend} />}
          </Box>
        </StatCard>
      </Box>

      {/* ── Weekly data table ────────────────────────────────────── */}
      {currentPeriodData.weekly_data && currentPeriodData.weekly_data.length > 0 ? (
        <TableContainer component={Paper} sx={{ bgcolor: 'background.paper', mb: 2 }}>
          <Table size="small" aria-label="Weekly utilization data">
            <TableHead>
              <TableRow sx={{ '& .MuiTableCell-head': { color: TEXT_SECONDARY, fontSize: '0.7rem', letterSpacing: '0.05em', textTransform: 'uppercase' } }}>
                <TableCell>Week Ending</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Project</TableCell>
                <TableCell align="right">Billable Hrs</TableCell>
                <TableCell align="right">Non-Billable Hrs</TableCell>
                <TableCell align="right">Capacity</TableCell>
                <TableCell align="center">Utilization</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentPeriodData.weekly_data.map((week, index) => (
                <TableRow key={index} hover>
                  <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                    {formatDate(week.week_ending)}
                  </TableCell>
                  <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                    <ClientNameLink client={{ client_name: week.client_name || week.client }}>
                      {week.client_name || week.client || 'Unknown'}
                    </ClientNameLink>
                  </TableCell>
                  <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                    <ProjectNameLink project={{ project_id: week.project_id, name: week.project_name, client_name: week.client_name }}>
                      {week.project_name ||
                        week.formatted_project ||
                        (week.project && typeof week.project === 'string' && week.project.startsWith('P')
                          ? `Project ${week.project.substring(1)}`
                          : week.project || 'Unknown')}
                    </ProjectNameLink>
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                    {formatHours(week.billable_hours, 1)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: COLOR_WARN, fontSize: '0.8rem' }}>
                    {formatHours(week.non_billable_hours || 0, 1)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                    {week.available_hours}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: getUtilizationColor(week.utilization) }}>
                        {formatPercentage(week.utilization, 1)}
                      </Typography>
                      <Box sx={{ width: 40, height: 5, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.08)' }}>
                        <Box sx={{ width: `${Math.min(week.utilization, 100)}%`, height: '100%', borderRadius: 3, bgcolor: getUtilizationColor(week.utilization) }} />
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <EmptyState
          title="No utilization data available"
          message="This employee has no recorded timesheet data in the selected period"
        />
      )}

      {/* ── Footer ───────────────────────────────────────────────── */}
      {currentPeriodData.last_updated && (
        <Typography variant="caption" sx={{ display: 'block', mt: 2, color: TEXT_SECONDARY }}>
          Last updated: {formatDate(currentPeriodData.last_updated)}
        </Typography>
      )}
    </Wrap>
  );
};

UtilizationDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  employeeName: PropTypes.string.isRequired,
  employeeId: PropTypes.string.isRequired,
  tabMode: PropTypes.bool,
};

export default UtilizationDetailsModal;
